import { create } from 'zustand';
import { useAccount } from 'wagmi';
import {
  getDeletedScheduleTombstoneStorageKey,
  persistDeletedScheduleTombstone,
  readDeletedScheduleTombstonesResult,
} from './deletedScheduleTombstones';

/**
 * Shared per-schedule transaction state.
 *
 * Locking and receipt tracking must survive modal unmounts (closing the
 * schedule dialog unmounts every modal), so this lives in a module-level
 * store instead of component state. Entries are scoped by
 * chainId + account + scheduleId so they cannot leak between wallets or
 * networks.
 *
 * Data-consistency model: the indexer only ingests finalized Rootstock
 * blocks (~6 min behind) and public RPC replicas can lag a few blocks, so
 * after a confirmed transaction we install an *override* recording the
 * pre-transaction value and the expected value. Overrides compose across
 * sequential actions, and the query layer reconciles them directionally so an
 * intermediate stale balance cannot increase what the user may withdraw. A
 * long safety TTL exists only as a backstop.
 */

export type ScheduleAction = 'deposit' | 'withdraw' | 'edit' | 'delete';
export type ScheduleActionStatus = 'submitting' | 'pending' | 'reconciling' | 'uncertain';
export type ScheduleTransactionPhase = 'approval' | 'action';

export type PlannedOverride =
  | { kind: 'delete' }
  | { kind: 'balance'; preTx: string; expected: string }
  | { kind: 'purchaseAmount'; preTx: string; expected: string }
  | { kind: 'purchasePeriod'; preTx: string; expected: string };

export type OverrideField = 'balance' | 'purchaseAmount' | 'purchasePeriod';

export interface PendingScheduleAction {
  chainId: number;
  account: string;
  scheduleId: string;
  token: string;
  scheduleIndex: number;
  action: ScheduleAction;
  status: ScheduleActionStatus;
  transactionPhase: ScheduleTransactionPhase;
  hash?: `0x${string}`;
  submittedAt: number;
  /** When the action receipt was first observed and reconciliation began. */
  confirmedAt?: number;
  /** Identifies the exact local wallet request that owns a hashless reservation. */
  reservationId?: string;
  /** Required to continue an approve -> deposit flow after unmount/refresh. */
  depositAmount?: string;
  override?: PlannedOverride;
}

export interface ScheduleFieldOverride {
  scheduleId: string;
  kind: OverrideField;
  preTx: string;
  /** Value established by the latest action receipt block. */
  confirmedExpected: string;
  /** Conservative display value; balances may ratchet down while sources converge. */
  expected: string;
  createdAt: number;
}

export interface ScheduleOrderOverride {
  token: string;
  /** Authoritative schedule-id order read from the delete receipt block. */
  scheduleIds: string[];
  createdAt: number;
}

interface ScheduleActionStoreState {
  /** Keyed by `${chainId}:${account}:${scheduleId}` (lowercased). */
  pending: Record<string, PendingScheduleAction>;
  /** Keyed by scope `${chainId}:${account}`; value keyed by `${scheduleId}:${kind}`. */
  overrides: Record<string, Record<string, ScheduleFieldOverride>>;
  /** Keyed by scope; value keyed by lowercased token address. */
  orderOverrides: Record<string, Record<string, ScheduleOrderOverride>>;
  /** Keyed by scope; deleted schedule ids (mirrors persisted tombstones). */
  tombstones: Record<string, Set<string>>;
  hydratedScopes: Record<string, boolean>;
}

/**
 * Rootstock-backed API data only includes finalized blocks (~6 minutes).
 * Keep one extra minute of margin before a mutable RPC value may retire an
 * authoritative receipt-block override.
 */
export const ROOTSTOCK_FINALITY_STABILITY_MS = 7 * 60 * 1000;
/** Field values stop overriding direct chain reads once the finality margin passes. */
const FIELD_OVERRIDE_SAFETY_TTL_MS = ROOTSTOCK_FINALITY_STABILITY_MS;
/** Order overrides also self-expire on a later hydration if no chain read retired them. */
const ORDER_OVERRIDE_SAFETY_TTL_MS = 30 * 60 * 1000;
/** Pending entries that never obtained a hash are abandoned after this (wallet prompt lost). */
const SUBMITTING_STALE_MS = 5 * 60 * 1000;
/** Active wallet promises in this tab must never be removed by stale cleanup. */
const activeSubmissionReservations = new Set<string>();
/** Preserve in-memory truth when a scope could not be written durably. */
const unreliablePendingStorageScopes = new Set<string>();
const unreliableOverrideStorageScopes = new Set<string>();
const unreliableOrderOverrideStorageScopes = new Set<string>();
const unreliableTombstoneStorageScopes = new Set<string>();
let reservationSequence = 0;

function createReservationId(): string {
  const cryptoApi = Reflect.get(globalThis, 'crypto') as Crypto | undefined;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  reservationSequence += 1;
  return `${Date.now().toString(36)}:${reservationSequence.toString(36)}:${Math.random().toString(36).slice(2)}`;
}

export function scopeKey(chainId: number, account: string): string {
  return `${String(chainId)}:${account.toLowerCase()}`;
}

export function actionKey(chainId: number, account: string, scheduleId: string): string {
  return `${scopeKey(chainId, account)}:${scheduleId.toLowerCase()}`;
}

function overrideKey(scheduleId: string, kind: OverrideField): string {
  return `${scheduleId.toLowerCase()}:${kind}`;
}

function pendingStorageKey(scope: string): string {
  return `bitchill:pending-schedule-actions:${scope}`;
}

function overridesStorageKey(scope: string): string {
  return `bitchill:schedule-overrides:${scope}`;
}

function orderOverridesStorageKey(scope: string): string {
  return `bitchill:schedule-order-overrides:${scope}`;
}

function submissionLockName(chainId: number, account: string): string {
  return `bitchill:schedule-submission:${scopeKey(chainId, account)}`;
}

function getStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

interface JsonArrayReadResult {
  entries: unknown[];
  storageReadable: boolean;
}

function readJsonArray(key: string): JsonArrayReadResult {
  const storage = getStorage();
  if (!storage) return { entries: [], storageReadable: false };
  try {
    const raw = storage.getItem(key);
    if (!raw) return { entries: [], storageReadable: true };
    const parsed: unknown = JSON.parse(raw);
    return { entries: Array.isArray(parsed) ? parsed : [], storageReadable: true };
  } catch {
    return { entries: [], storageReadable: false };
  }
}

function omitKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));
}

function writeJson(key: string, value: unknown, isEmpty: boolean): boolean {
  const storage = getStorage();
  if (!storage) return false;
  try {
    if (isEmpty) {
      storage.removeItem(key);
      return true;
    }
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function persistPendingScope(scope: string, pending: Record<string, PendingScheduleAction>): boolean {
  // Only post-hash entries are persisted; a lost wallet prompt must not lock forever.
  const entries = Object.values(pending).filter(
    (entry) => scopeKey(entry.chainId, entry.account) === scope && entry.hash !== undefined,
  );
  const persisted = writeJson(pendingStorageKey(scope), entries, entries.length === 0);
  if (persisted) unreliablePendingStorageScopes.delete(scope);
  else unreliablePendingStorageScopes.add(scope);
  return persisted;
}

function persistOverridesScope(scope: string, overrides: Record<string, ScheduleFieldOverride>): boolean {
  const entries = Object.values(overrides);
  const persisted = writeJson(overridesStorageKey(scope), entries, entries.length === 0);
  if (persisted) unreliableOverrideStorageScopes.delete(scope);
  else unreliableOverrideStorageScopes.add(scope);
  return persisted;
}

function persistOrderOverridesScope(
  scope: string,
  overrides: Record<string, ScheduleOrderOverride>,
): boolean {
  const entries = Object.values(overrides);
  const persisted = writeJson(orderOverridesStorageKey(scope), entries, entries.length === 0);
  if (persisted) unreliableOrderOverrideStorageScopes.delete(scope);
  else unreliableOrderOverrideStorageScopes.add(scope);
  return persisted;
}

function isValidPersistedAction(entry: unknown): entry is PendingScheduleAction {
  if (!entry || typeof entry !== 'object') return false;
  const record = entry as Record<string, unknown>;
  return (
    typeof record.chainId === 'number' &&
    typeof record.account === 'string' &&
    typeof record.scheduleId === 'string' &&
    typeof record.token === 'string' &&
    typeof record.scheduleIndex === 'number' &&
    typeof record.action === 'string' &&
    (record.transactionPhase === 'approval' || record.transactionPhase === 'action') &&
    typeof record.submittedAt === 'number' &&
    (record.confirmedAt === undefined || typeof record.confirmedAt === 'number') &&
    typeof record.hash === 'string' &&
    (record.reservationId === undefined || typeof record.reservationId === 'string')
  );
}

type PersistedScheduleFieldOverride = Omit<ScheduleFieldOverride, 'confirmedExpected'> & {
  confirmedExpected?: string;
};

function isValidPersistedOverride(entry: unknown): entry is PersistedScheduleFieldOverride {
  if (!entry || typeof entry !== 'object') return false;
  const record = entry as Record<string, unknown>;
  return (
    typeof record.scheduleId === 'string' &&
    (record.kind === 'balance' || record.kind === 'purchaseAmount' || record.kind === 'purchasePeriod') &&
    typeof record.preTx === 'string' &&
    (record.confirmedExpected === undefined || typeof record.confirmedExpected === 'string') &&
    typeof record.expected === 'string' &&
    typeof record.createdAt === 'number'
  );
}

function isValidPersistedOrderOverride(entry: unknown): entry is ScheduleOrderOverride {
  if (!entry || typeof entry !== 'object') return false;
  const record = entry as Record<string, unknown>;
  return (
    typeof record.token === 'string' &&
    Array.isArray(record.scheduleIds) &&
    record.scheduleIds.every((scheduleId) => typeof scheduleId === 'string') &&
    typeof record.createdAt === 'number'
  );
}

function readPersistedPendingScope(
  scope: string,
): { pending: Record<string, PendingScheduleAction>; storageReadable: boolean } {
  const pending: Record<string, PendingScheduleAction> = {};
  const read = readJsonArray(pendingStorageKey(scope));
  for (const entry of read.entries) {
    if (!isValidPersistedAction(entry)) continue;
    if (scopeKey(entry.chainId, entry.account) !== scope) continue;
    pending[actionKey(entry.chainId, entry.account, entry.scheduleId)] = {
      ...entry,
      status:
        entry.status === 'reconciling' || entry.status === 'uncertain'
          ? entry.status
          : 'pending',
    };
  }
  return { pending, storageReadable: read.storageReadable };
}

function readPersistedOverrideScope(
  scope: string,
  nowMs: number,
): { overrides: Record<string, ScheduleFieldOverride>; storageReadable: boolean } {
  const overrides: Record<string, ScheduleFieldOverride> = {};
  const read = readJsonArray(overridesStorageKey(scope));
  for (const entry of read.entries) {
    if (!isValidPersistedOverride(entry)) continue;
    if (nowMs - entry.createdAt > FIELD_OVERRIDE_SAFETY_TTL_MS) continue;
    overrides[overrideKey(entry.scheduleId, entry.kind)] = {
      ...entry,
      confirmedExpected: entry.confirmedExpected ?? entry.expected,
    };
  }
  return { overrides, storageReadable: read.storageReadable };
}

function readPersistedOrderOverrideScope(
  scope: string,
  nowMs: number,
): { overrides: Record<string, ScheduleOrderOverride>; storageReadable: boolean } {
  const overrides: Record<string, ScheduleOrderOverride> = {};
  const read = readJsonArray(orderOverridesStorageKey(scope));
  for (const entry of read.entries) {
    if (!isValidPersistedOrderOverride(entry)) continue;
    if (nowMs - entry.createdAt > ORDER_OVERRIDE_SAFETY_TTL_MS) continue;
    const token = entry.token.toLowerCase();
    overrides[token] = {
      token,
      scheduleIds: entry.scheduleIds.map((scheduleId) => scheduleId.toLowerCase()),
      createdAt: entry.createdAt,
    };
  }
  return { overrides, storageReadable: read.storageReadable };
}

export const useScheduleActionStore = create<ScheduleActionStoreState>(() => ({
  pending: {},
  overrides: {},
  orderOverrides: {},
  tombstones: {},
  hydratedScopes: {},
}));

/** Test helper: reset in-memory state (does not touch localStorage). */
export function resetScheduleActionStore(): void {
  activeSubmissionReservations.clear();
  unreliablePendingStorageScopes.clear();
  unreliableOverrideStorageScopes.clear();
  unreliableOrderOverrideStorageScopes.clear();
  unreliableTombstoneStorageScopes.clear();
  useScheduleActionStore.setState({
    pending: {},
    overrides: {},
    orderOverrides: {},
    tombstones: {},
    hydratedScopes: {},
  });
}

/**
 * Load persisted pending actions, overrides and tombstones for a wallet scope.
 * Idempotent; call again after account/chain changes.
 */
export function hydrateScheduleActionScope(chainId: number, account: string, nowMs = Date.now()): void {
  // Re-read even when this scope was hydrated before. Account switching or a
  // disconnected tab can miss storage events from another active tab.
  syncScheduleActionScopeFromStorage(chainId, account, nowMs);
}

/**
 * Refresh one wallet scope after another tab changes transaction storage.
 * Hashless local reservations are retained while their wallet request is open.
 */
export function syncScheduleActionScopeFromStorage(
  chainId: number,
  account: string,
  nowMs = Date.now(),
): void {
  const scope = scopeKey(chainId, account);
  const pendingRead = readPersistedPendingScope(scope);
  const overrideRead = readPersistedOverrideScope(scope, nowMs);
  const orderOverrideRead = readPersistedOrderOverrideScope(scope, nowMs);
  const tombstoneRead = readDeletedScheduleTombstonesResult(chainId, account, nowMs);

  useScheduleActionStore.setState((current) => {
    const outsideScope = Object.fromEntries(
      Object.entries(current.pending).filter(
        ([, entry]) => scopeKey(entry.chainId, entry.account) !== scope,
      ),
    );
    const currentScopePending = Object.fromEntries(
      Object.entries(current.pending).filter(
        ([, entry]) =>
          scopeKey(entry.chainId, entry.account) === scope,
      ),
    );
    const localHashless = Object.fromEntries(
      Object.entries(currentScopePending).filter(([, entry]) => entry.hash === undefined),
    );
    const activeLocalHashless = Object.fromEntries(
      Object.entries(localHashless).filter(
        ([, entry]) =>
          entry.reservationId !== undefined &&
          activeSubmissionReservations.has(entry.reservationId),
      ),
    );
    const canTrustPendingStorage =
      pendingRead.storageReadable && !unreliablePendingStorageScopes.has(scope);
    const canTrustOverrideStorage =
      overrideRead.storageReadable && !unreliableOverrideStorageScopes.has(scope);
    const canTrustOrderOverrideStorage =
      orderOverrideRead.storageReadable && !unreliableOrderOverrideStorageScopes.has(scope);
    const canTrustTombstoneStorage =
      tombstoneRead.storageReadable && !unreliableTombstoneStorageScopes.has(scope);

    return {
      // A storage event cannot steal a slot from a wallet promise this tab is
      // actively awaiting. On supported browsers the account Web Lock also
      // prevents another tab from creating that conflict in the first place.
      pending: {
        ...outsideScope,
        ...(canTrustPendingStorage ? localHashless : currentScopePending),
        ...(canTrustPendingStorage ? pendingRead.pending : {}),
        ...activeLocalHashless,
      },
      overrides: {
        ...current.overrides,
        [scope]: canTrustOverrideStorage
          ? overrideRead.overrides
          : current.overrides[scope] ?? {},
      },
      orderOverrides: {
        ...current.orderOverrides,
        [scope]: canTrustOrderOverrideStorage
          ? orderOverrideRead.overrides
          : current.orderOverrides[scope] ?? {},
      },
      tombstones: {
        ...current.tombstones,
        [scope]: canTrustTombstoneStorage
          ? tombstoneRead.tombstones
          : current.tombstones[scope] ?? new Set<string>(),
      },
      hydratedScopes: { ...current.hydratedScopes, [scope]: true },
    };
  });
}

export function isScheduleActionStorageKey(
  key: string | null,
  chainId: number,
  account: string,
): boolean {
  if (!key) return false;
  const scope = scopeKey(chainId, account);
  return (
    key === pendingStorageKey(scope) ||
    key === overridesStorageKey(scope) ||
    key === orderOverridesStorageKey(scope) ||
    key === getDeletedScheduleTombstoneStorageKey(chainId, account)
  );
}

export interface BeginActionParams {
  chainId: number;
  account: string;
  scheduleId: string;
  token: string;
  scheduleIndex: number;
  action: ScheduleAction;
  depositAmount?: string;
  override?: PlannedOverride;
  nowMs?: number;
}

export async function withScheduleSubmissionLock<T>(
  chainId: number,
  account: string,
  callback: () => Promise<T>,
): Promise<T> {
  const lockManager = typeof navigator === 'undefined'
    ? undefined
    : Reflect.get(navigator, 'locks') as LockManager | undefined;
  if (!lockManager) return callback();
  return lockManager.request(
    submissionLockName(chainId, account),
    { mode: 'exclusive' },
    callback,
  );
}

export class ScheduleSubmissionBusyError extends Error {
  constructor() {
    super('Another wallet request is already in progress for this account');
    this.name = 'ScheduleSubmissionBusyError';
  }
}

export function isScheduleSubmissionBusyError(error: unknown): boolean {
  return error instanceof ScheduleSubmissionBusyError;
}

/**
 * User-initiated actions must never sit in an invisible queue behind another
 * tab's wallet prompt. Acquire immediately or ask the user to retry.
 */
async function withAvailableScheduleSubmissionLock<T>(
  chainId: number,
  account: string,
  callback: () => Promise<T>,
): Promise<T> {
  const lockManager = typeof navigator === 'undefined'
    ? undefined
    : Reflect.get(navigator, 'locks') as LockManager | undefined;
  if (!lockManager) return callback();
  return lockManager.request(
    submissionLockName(chainId, account),
    { mode: 'exclusive', ifAvailable: true },
    (lock) => {
      if (lock === null) throw new ScheduleSubmissionBusyError();
      return callback();
    },
  );
}

/**
 * Register an action lock before requesting the wallet signature.
 * Returns false (and does nothing) if the schedule already has an active action.
 */
export function beginScheduleAction(params: BeginActionParams, reservationId?: string): boolean {
  const key = actionKey(params.chainId, params.account, params.scheduleId);
  const state = useScheduleActionStore.getState();
  if (state.pending[key]) return false;

  const scope = scopeKey(params.chainId, params.account);
  const normalizedScheduleId = params.scheduleId.toLowerCase();
  // A final storage sync may reveal a delete that this render has not observed
  // yet. Never submit another action for a confirmed tombstone.
  if (state.tombstones[scope]?.has(normalizedScheduleId)) return false;

  const token = params.token.toLowerCase();
  const tokenActions = Object.values(state.pending).filter(
    (entry) => scopeKey(entry.chainId, entry.account) === scope && entry.token === token,
  );
  // deleteDcaSchedule uses swap-and-pop. A delete can change another schedule's
  // index, so it must be mutually exclusive with every action for that token.
  if (
    tokenActions.some((entry) => entry.action === 'delete') ||
    (params.action === 'delete' && tokenActions.length > 0)
  ) {
    return false;
  }

  let plannedOverride = params.override;
  if (plannedOverride && plannedOverride.kind !== 'delete') {
    const existing = state.overrides[scope]?.[overrideKey(params.scheduleId, plannedOverride.kind)];
    // Chain sequential confirmed actions while a replica/indexer still returns
    // the original value. This keeps one stable raw baseline and advances only
    // the expected value.
    if (existing) {
      // If this handler was created from a stale render, do not send using its
      // obsolete baseline. React will re-render with the synced expected value.
      if (existing.expected !== plannedOverride.preTx) return false;
      plannedOverride = { ...plannedOverride, preTx: existing.preTx };
    }
  }

  useScheduleActionStore.setState((current) => ({
    pending: {
      ...current.pending,
      [key]: {
        chainId: params.chainId,
        account: params.account.toLowerCase(),
        scheduleId: params.scheduleId.toLowerCase(),
        token,
        scheduleIndex: params.scheduleIndex,
        action: params.action,
        status: 'submitting',
        transactionPhase: 'action',
        submittedAt: params.nowMs ?? Date.now(),
        reservationId,
        depositAmount: params.depositAmount,
        override: plannedOverride,
      },
    },
  }));
  return true;
}

/**
 * Serialize the final shared-state check, wallet request and hash persistence
 * across every tab using the same wallet account.
 */
export async function submitScheduleAction(
  params: BeginActionParams,
  sendTransaction: () => Promise<`0x${string}`>,
  transactionPhase: ScheduleTransactionPhase = 'action',
): Promise<boolean> {
  return withAvailableScheduleSubmissionLock(params.chainId, params.account, async () => {
    syncScheduleActionScopeFromStorage(params.chainId, params.account);
    const reservationId = createReservationId();
    if (!beginScheduleAction(params, reservationId)) return false;
    activeSubmissionReservations.add(reservationId);

    try {
      const hash = await sendTransaction();
      const tracked = markScheduleActionSubmitted(
        params.chainId,
        params.account,
        params.scheduleId,
        hash,
        transactionPhase,
        Date.now(),
        reservationId,
      );
      if (!tracked) {
        throw new Error(`Submitted transaction ${hash} lost its action reservation`);
      }
      return true;
    } catch (error) {
      clearScheduleActionReservation(
        params.chainId,
        params.account,
        params.scheduleId,
        reservationId,
      );
      throw error;
    } finally {
      activeSubmissionReservations.delete(reservationId);
    }
  });
}

/** Transaction submitted: attach the hash and persist so a refresh resumes tracking. */
export function markScheduleActionSubmitted(
  chainId: number,
  account: string,
  scheduleId: string,
  hash: `0x${string}`,
  transactionPhase: ScheduleTransactionPhase = 'action',
  nowMs = Date.now(),
  expectedReservationId?: string,
): boolean {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  let submitted = false;
  useScheduleActionStore.setState((current) => {
    const entry = current.pending[key];
    if (!entry) return current;
    if (
      expectedReservationId !== undefined &&
      entry.reservationId !== expectedReservationId
    ) {
      return current;
    }
    submitted = true;
    const pending = {
      ...current.pending,
      [key]: {
        ...entry,
        status: 'pending' as const,
        transactionPhase,
        hash,
        submittedAt: nowMs,
      },
    };
    persistPendingScope(scope, pending);
    return { pending };
  });
  return submitted;
}

/** Adopt a same-input speed-up without allowing a stale callback to win. */
export function adoptScheduleActionReplacement(
  chainId: number,
  account: string,
  scheduleId: string,
  replacedHash: `0x${string}`,
  replacementHash: `0x${string}`,
  nowMs = Date.now(),
): boolean {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  let adopted = false;
  useScheduleActionStore.setState((current) => {
    const entry = current.pending[key];
    if (!entry || entry.hash?.toLowerCase() !== replacedHash.toLowerCase()) return current;
    adopted = true;
    const pending = {
      ...current.pending,
      [key]: {
        ...entry,
        hash: replacementHash,
        status: 'pending' as const,
        submittedAt: nowMs,
      },
    };
    persistPendingScope(scope, pending);
    return { pending };
  });
  return adopted;
}

/** Read one action directly from shared storage, bypassing this tab's Zustand state. */
export function readPersistedPendingScheduleAction(
  chainId: number,
  account: string,
  scheduleId: string,
): PendingScheduleAction | undefined {
  const scope = scopeKey(chainId, account);
  const targetKey = actionKey(chainId, account, scheduleId);
  const read = readJsonArray(pendingStorageKey(scope));
  if (!read.storageReadable || unreliablePendingStorageScopes.has(scope)) {
    return getPendingScheduleAction(chainId, account, scheduleId);
  }
  return read.entries.find((candidate) => {
    if (!isValidPersistedAction(candidate)) return false;
    return actionKey(candidate.chainId, candidate.account, candidate.scheduleId) === targetKey;
  }) as PendingScheduleAction | undefined;
}

/** Bring one in-memory action in line with the latest cross-tab persisted value. */
export function syncPendingScheduleActionFromStorage(
  chainId: number,
  account: string,
  scheduleId: string,
): PendingScheduleAction | undefined {
  syncScheduleActionScopeFromStorage(chainId, account);
  return getPendingScheduleAction(chainId, account, scheduleId);
}

/**
 * Reserve the deposit follow-up step after approval confirms.
 * Returns true for a fresh claim (`pending` -> `submitting`) or a refresh resume
 * (`submitting` while still in the `approval` phase). False once `action` starts.
 */
export function claimDepositFollowUp(
  chainId: number,
  account: string,
  scheduleId: string,
): boolean {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  let claimed = false;
  useScheduleActionStore.setState((current) => {
    const entry = current.pending[key];
    if (!entry || entry.transactionPhase !== 'approval') return current;
    if (entry.status !== 'pending' && entry.status !== 'submitting') return current;
    claimed = true;
    if (entry.status === 'pending') {
      const pending = { ...current.pending, [key]: { ...entry, status: 'submitting' as const } };
      persistPendingScope(scope, pending);
      return { pending };
    }
    return current;
  });
  return claimed;
}

/** Preserve a deposit approval lock when the follow-up broadcast outcome is ambiguous. */
export function markDepositFollowUpUncertain(
  chainId: number,
  account: string,
  scheduleId: string,
  nowMs = Date.now(),
): boolean {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  let marked = false;
  useScheduleActionStore.setState((current) => {
    const entry = current.pending[key];
    if (!entry || entry.action !== 'deposit' || entry.transactionPhase !== 'approval') {
      return current;
    }
    marked = true;
    const pending = {
      ...current.pending,
      [key]: { ...entry, status: 'uncertain' as const, submittedAt: nowMs },
    };
    persistPendingScope(scope, pending);
    return { pending };
  });
  return marked;
}

/** Remove the lock (wallet rejection, submission failure, reverted receipt, completion). */
export function clearScheduleAction(chainId: number, account: string, scheduleId: string): void {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  useScheduleActionStore.setState((current) => {
    if (!current.pending[key]) return current;
    const pending = omitKey(current.pending, key);
    persistPendingScope(scope, pending);
    return { pending };
  });
}

/** Release only the exact wallet request that created this reservation. */
function clearScheduleActionReservation(
  chainId: number,
  account: string,
  scheduleId: string,
  reservationId: string,
): boolean {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  let cleared = false;
  useScheduleActionStore.setState((current) => {
    const entry = current.pending[key];
    if (
      !entry ||
      entry.hash !== undefined ||
      entry.reservationId !== reservationId
    ) {
      return current;
    }
    cleared = true;
    const pending = omitKey(current.pending, key);
    persistPendingScope(scope, pending);
    return { pending };
  });
  return cleared;
}

/** Clear only if the tracked hash is still the transaction being replaced. */
export function clearScheduleActionForHash(
  chainId: number,
  account: string,
  scheduleId: string,
  expectedHash: `0x${string}`,
): boolean {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  let cleared = false;
  useScheduleActionStore.setState((current) => {
    const entry = current.pending[key];
    if (!entry || entry.hash?.toLowerCase() !== expectedHash.toLowerCase()) return current;
    cleared = true;
    const pending = omitKey(current.pending, key);
    persistPendingScope(scope, pending);
    return { pending };
  });
  return cleared;
}

/**
 * Receipt confirmed: apply the planned optimistic override (or tombstone for
 * deletes), move the entry to `reconciling`, and persist both.
 */
export function confirmScheduleAction(
  chainId: number,
  account: string,
  scheduleId: string,
  nowMs = Date.now(),
): void {
  const key = actionKey(chainId, account, scheduleId);
  const scope = scopeKey(chainId, account);
  const entry = useScheduleActionStore.getState().pending[key];
  if (!entry) return;

  if (entry.override?.kind === 'delete' || entry.action === 'delete') {
    const tombstonePersisted = persistDeletedScheduleTombstone(
      chainId,
      account,
      scheduleId,
    );
    if (tombstonePersisted) unreliableTombstoneStorageScopes.delete(scope);
    else unreliableTombstoneStorageScopes.add(scope);
    useScheduleActionStore.setState((current) => {
      const scopeTombstones = new Set(current.tombstones[scope] ?? []);
      scopeTombstones.add(scheduleId.toLowerCase());
      const pending = {
        ...current.pending,
        [key]: { ...entry, status: 'reconciling' as const, confirmedAt: nowMs },
      };
      persistPendingScope(scope, pending);
      return {
        tombstones: { ...current.tombstones, [scope]: scopeTombstones },
        pending,
      };
    });
    return;
  }

  useScheduleActionStore.setState((current) => {
    const scopeOverrides = { ...(current.overrides[scope] ?? {}) };
    if (entry.override && entry.override.kind !== 'delete') {
      const override: ScheduleFieldOverride = {
        scheduleId: scheduleId.toLowerCase(),
        kind: entry.override.kind,
        preTx: entry.override.preTx,
        confirmedExpected: entry.override.expected,
        expected: entry.override.expected,
        createdAt: nowMs,
      };
      scopeOverrides[overrideKey(scheduleId, override.kind)] = override;
      persistOverridesScope(scope, scopeOverrides);
    }
    const pending = {
      ...current.pending,
      [key]: { ...entry, status: 'reconciling' as const, confirmedAt: nowMs },
    };
    persistPendingScope(scope, pending);
    return {
      overrides: { ...current.overrides, [scope]: scopeOverrides },
      pending,
    };
  });
}

/** Replace a predicted value with the value read from the receipt block. */
export function setReconciledScheduleValue(
  chainId: number,
  account: string,
  scheduleId: string,
  kind: OverrideField,
  expected: string,
): void {
  const scope = scopeKey(chainId, account);
  useScheduleActionStore.setState((current) => {
    const scopeOverrides = { ...(current.overrides[scope] ?? {}) };
    const key = overrideKey(scheduleId, kind);
    const existing = scopeOverrides[key];
    if (!existing) return current;
    // A live query may already have observed a later purchase and ratcheted a
    // balance below the receipt-block value while this historical read was in
    // flight. Never raise that conservative low-water mark back up.
    const conservativeExpected = kind === 'balance'
      ? (BigInt(existing.expected) < BigInt(expected) ? existing.expected : expected)
      : expected;
    scopeOverrides[key] = {
      ...existing,
      confirmedExpected: expected,
      expected: conservativeExpected,
    };
    persistOverridesScope(scope, scopeOverrides);
    return { overrides: { ...current.overrides, [scope]: scopeOverrides } };
  });
}

/**
 * Remember a lower observed balance without allowing a later lagging replica
 * to raise the UI back above it. The receipt-block value remains separate so
 * finality reconciliation still has an immutable reference point.
 */
export function ratchetScheduleBalanceOverride(
  chainId: number,
  account: string,
  scheduleId: string,
  observed: string,
): void {
  const scope = scopeKey(chainId, account);
  useScheduleActionStore.setState((current) => {
    const scopeOverrides = current.overrides[scope];
    const key = overrideKey(scheduleId, 'balance');
    const existing = scopeOverrides?.[key];
    if (!existing || BigInt(observed) >= BigInt(existing.expected)) return current;
    const nextScope = {
      ...scopeOverrides,
      [key]: { ...existing, expected: observed },
    };
    persistOverridesScope(scope, nextScope);
    return { overrides: { ...current.overrides, [scope]: nextScope } };
  });
}

/** Persist the authoritative swap-and-pop order read from a delete receipt block. */
export function setReconciledScheduleOrder(
  chainId: number,
  account: string,
  token: string,
  scheduleIds: readonly string[],
  nowMs = Date.now(),
): void {
  const scope = scopeKey(chainId, account);
  const normalizedToken = token.toLowerCase();
  const normalizedIds = scheduleIds.map((scheduleId) => scheduleId.toLowerCase());
  if (new Set(normalizedIds).size !== normalizedIds.length) {
    throw new Error('Duplicate schedule id in reconciled token order');
  }
  useScheduleActionStore.setState((current) => {
    const scopeOverrides = {
      ...(current.orderOverrides[scope] ?? {}),
      [normalizedToken]: {
        token: normalizedToken,
        scheduleIds: normalizedIds,
        createdAt: nowMs,
      },
    };
    persistOrderOverridesScope(scope, scopeOverrides);
    return { orderOverrides: { ...current.orderOverrides, [scope]: scopeOverrides } };
  });
}

/** Explicitly remove an override from both in-memory and persisted state. */
export function pruneScheduleOverride(
  chainId: number,
  account: string,
  scheduleId: string,
  kind: OverrideField,
): void {
  const scope = scopeKey(chainId, account);
  useScheduleActionStore.setState((current) => {
    const scopeOverrides = current.overrides[scope];
    const key = overrideKey(scheduleId, kind);
    if (!scopeOverrides?.[key]) return current;
    const next = omitKey(scopeOverrides, key);
    persistOverridesScope(scope, next);
    return { overrides: { ...current.overrides, [scope]: next } };
  });
}

/** Remove every field override whose finality safety window has elapsed. */
export function pruneExpiredScheduleFieldOverrides(
  chainId: number,
  account: string,
  nowMs = Date.now(),
): void {
  const scope = scopeKey(chainId, account);
  useScheduleActionStore.setState((current) => {
    const scopeOverrides = current.overrides[scope];
    if (!scopeOverrides) return current;
    const next = Object.fromEntries(
      Object.entries(scopeOverrides).filter(
        ([, override]) => nowMs - override.createdAt <= FIELD_OVERRIDE_SAFETY_TTL_MS,
      ),
    );
    if (Object.keys(next).length === Object.keys(scopeOverrides).length) return current;
    persistOverridesScope(scope, next);
    return { overrides: { ...current.overrides, [scope]: next } };
  });
}

/** Stop using a receipt-block order once a current chain read proves it is obsolete. */
export function pruneScheduleOrderOverride(
  chainId: number,
  account: string,
  token: string,
): void {
  const scope = scopeKey(chainId, account);
  const normalizedToken = token.toLowerCase();
  useScheduleActionStore.setState((current) => {
    const scopeOverrides = current.orderOverrides[scope];
    if (!scopeOverrides?.[normalizedToken]) return current;
    const next = omitKey(scopeOverrides, normalizedToken);
    persistOrderOverridesScope(scope, next);
    return { orderOverrides: { ...current.orderOverrides, [scope]: next } };
  });
}

/** Abandon `submitting` entries whose wallet prompt outcome we can no longer observe. */
export function pruneStaleSubmittingActions(nowMs = Date.now()): void {
  useScheduleActionStore.setState((current) => {
    const isStale = (entry: PendingScheduleAction) =>
      entry.status === 'submitting' &&
      !entry.hash &&
      nowMs - entry.submittedAt > SUBMITTING_STALE_MS &&
      (entry.reservationId === undefined ||
        !activeSubmissionReservations.has(entry.reservationId));
    if (!Object.values(current.pending).some(isStale)) return current;
    const pending = Object.fromEntries(
      Object.entries(current.pending).filter(([, entry]) => !isStale(entry)),
    );
    return { pending };
  });
}

export function getPendingScheduleAction(
  chainId: number,
  account: string,
  scheduleId: string,
): PendingScheduleAction | undefined {
  return useScheduleActionStore.getState().pending[actionKey(chainId, account, scheduleId)];
}

/** Reactive hook: the active action for a schedule under the connected wallet, if any. */
export function usePendingScheduleAction(scheduleId: string): PendingScheduleAction | undefined {
  const { address, chainId } = useAccount();
  return useScheduleActionStore((state) => {
    if (!address || chainId === undefined) return undefined;
    return state.pending[actionKey(chainId, address, scheduleId)];
  });
}

/**
 * Action that blocks a schedule: its own action, or a delete for the same token
 * because deletion can move this schedule to another array index.
 */
export function useBlockingScheduleAction(
  scheduleId: string,
  token: string,
): PendingScheduleAction | undefined {
  const { address, chainId } = useAccount();
  return useScheduleActionStore((state) => {
    if (!address || chainId === undefined) return undefined;
    const own = state.pending[actionKey(chainId, address, scheduleId)];
    if (own) return own;
    const scope = scopeKey(chainId, address);
    const normalizedToken = token.toLowerCase();
    return Object.values(state.pending).find(
      (entry) =>
        scopeKey(entry.chainId, entry.account) === scope &&
        entry.token === normalizedToken &&
        entry.action === 'delete',
    );
  });
}

/** Reactive hook: every pending action for the connected wallet scope. */
export function usePendingScheduleActions(): PendingScheduleAction[] {
  const { address, chainId } = useAccount();
  const pending = useScheduleActionStore((state) => state.pending);
  if (!address || chainId === undefined) return [];
  const scope = scopeKey(chainId, address);
  return Object.values(pending).filter((entry) => scopeKey(entry.chainId, entry.account) === scope);
}

/** Every active action for a token under the connected wallet scope. */
export function usePendingTokenScheduleActions(token: string): PendingScheduleAction[] {
  const { address, chainId } = useAccount();
  const pending = useScheduleActionStore((state) => state.pending);
  if (!address || chainId === undefined) return [];
  const scope = scopeKey(chainId, address);
  const normalizedToken = token.toLowerCase();
  return Object.values(pending).filter(
    (entry) =>
      scopeKey(entry.chainId, entry.account) === scope && entry.token === normalizedToken,
  );
}

const EMPTY_TOMBSTONES: ReadonlySet<string> = new Set<string>();
const EMPTY_OVERRIDES: Record<string, ScheduleFieldOverride> = {};
const EMPTY_ORDER_OVERRIDES: Record<string, ScheduleOrderOverride> = {};

/** Reactive hook: tombstoned schedule ids for the connected wallet scope. */
export function useScheduleTombstones(): ReadonlySet<string> {
  const { address, chainId } = useAccount();
  return useScheduleActionStore((state) => {
    if (!address || chainId === undefined) return EMPTY_TOMBSTONES;
    return state.tombstones[scopeKey(chainId, address)] ?? EMPTY_TOMBSTONES;
  });
}

/** Reactive hook: active field overrides for the connected wallet scope. */
export function useScheduleFieldOverrides(): Record<string, ScheduleFieldOverride> {
  const { address, chainId } = useAccount();
  return useScheduleActionStore((state) => {
    if (!address || chainId === undefined) return EMPTY_OVERRIDES;
    return state.overrides[scopeKey(chainId, address)] ?? EMPTY_OVERRIDES;
  });
}

/** Receipt-block schedule orders retained while public/API reads finalize. */
export function useScheduleOrderOverrides(): Record<string, ScheduleOrderOverride> {
  const { address, chainId } = useAccount();
  return useScheduleActionStore((state) => {
    if (!address || chainId === undefined) return EMPTY_ORDER_OVERRIDES;
    return state.orderOverrides[scopeKey(chainId, address)] ?? EMPTY_ORDER_OVERRIDES;
  });
}

export function getScheduleOrderOverride(
  overrides: Record<string, ScheduleOrderOverride>,
  token: string,
): ScheduleOrderOverride | undefined {
  return overrides[token.toLowerCase()];
}

export function getScheduleFieldOverride(
  overrides: Record<string, ScheduleFieldOverride>,
  scheduleId: string,
  kind: OverrideField,
): ScheduleFieldOverride | undefined {
  return overrides[overrideKey(scheduleId, kind)];
}
